import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    type ComponentPropsWithRef,
    type ReactNode,
    type Ref,
} from 'react';
import { createPortal } from 'react-dom';

import { IconTile } from '../icons/IconTile';
import type { IconName } from '../icons/registry';
import { cn } from '../lib/cn';
import { pushLayer } from '../lib/layers';
import { IconButton } from './IconButton';
import { MonoLabel } from './MonoLabel';

export type DrawerSide = 'left' | 'right';

/**
 * - `panel` shadow `0 0 30px` — work panels (opname tree and findings, forms)
 * - `rail`  shadow `0 0 40px` — the navigation rail as a drawer
 */
export type DrawerElevation = 'panel' | 'rail';

/**
 * - `fixed` — a modal layer over the whole app. It renders in a portal on
 *   `<body>`, so no stacking context, `overflow: hidden` or sticky bar of the
 *   screen that opened it can end up on top. While it is open the rest of the
 *   page is `inert` (not clickable, not focusable) and does not scroll.
 * - `absolute` — stays inside the nearest positioned ancestor and leaves
 *   everything outside it usable. Only for the opname grid, where the header,
 *   the progress bar and the button that opens the other panel stay in reach.
 */
export type DrawerPosition = 'fixed' | 'absolute';

export type DrawerProps = Omit<ComponentPropsWithRef<'div'>, 'title'> & {
    open: boolean;
    onClose: () => void;
    side?: DrawerSide;
    /** 246 for the rail, 274 for the tree, 318 for findings, 480–520 for a form. */
    width?: number;
    elevation?: DrawerElevation;
    position?: DrawerPosition;
    /** Dark panels (the rail) flip the surface and text colours. */
    surface?: 'light' | 'dark';
    /** Announced title. Required: a dialog without a name is unusable by screen reader. */
    title: string;
    children: ReactNode;
};

/**
 * Where a floating list inside an open drawer renders: inside the drawer's own
 * layer, so the page being inert never swallows it.
 */
const LayerRootContext = createContext<HTMLElement | null>(null);

/** The element a popover should portal into: the open drawer's layer, else `<body>`. */
export function useLayerRoot(): HTMLElement | null {
    return useContext(LayerRootContext) ?? (typeof document === 'undefined' ? null : document.body);
}

/**
 * Sliding panel with a scrim. Appears instantly — no transition. This is a tablet
 * in a crawl space; nobody waits for an animation (see "Animatie" in the handoff).
 *
 * Escape closes it (unless a list inside it is open: that closes first), a click
 * on the scrim closes it, focus moves into the panel on open and returns to the
 * trigger on close, and Tab is kept inside while it is open.
 */
export function Drawer({ open, ...props }: DrawerProps) {
    if (!open) {
        return null;
    }

    return <OpenDrawer {...props} />;
}

function OpenDrawer({
    onClose,
    side = 'left',
    width = 274,
    elevation = 'panel',
    position = 'fixed',
    surface = 'light',
    title,
    className,
    style,
    ref,
    children,
    ...rest
}: Omit<DrawerProps, 'open'>) {
    const modal = position === 'fixed';
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [layerRoot, setLayerRoot] = useState<HTMLDivElement | null>(null);

    /* The latest `onClose` without re-running the effect: a new arrow function on
       every render of the screen must not move focus back to the panel mid-typing. */
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const panel = panelRef.current;
        const restoreTo =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;

        const release = pushLayer({
            modal,
            element: modal ? (panel?.parentElement ?? null) : panel,
            onEscape: () => onCloseRef.current(),
        });

        panel?.focus({ preventScroll: true });

        function keepTabInside(event: globalThis.KeyboardEvent) {
            if (event.key !== 'Tab' || !panel) return;

            const focusable = Array.from(
                panel.querySelectorAll<HTMLElement>(
                    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                ),
            );
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (!first || !last) {
                event.preventDefault();
                return;
            }

            const active = document.activeElement;
            const outside = !panel.contains(active);

            if (event.shiftKey && (active === first || active === panel || outside)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (active === last || outside)) {
                event.preventDefault();
                first.focus();
            }
        }

        document.addEventListener('keydown', keepTabInside, true);

        return () => {
            document.removeEventListener('keydown', keepTabInside, true);
            /* First give the page back (inert off), then focus the trigger in it. */
            release();

            if (restoreTo?.isConnected) {
                restoreTo.focus({ preventScroll: true });
            }
        };
    }, [modal]);

    const placement = modal ? 'fixed' : 'absolute';

    const layer = (
        <>
            <div
                aria-hidden="true"
                data-drawer-scrim=""
                onClick={() => onCloseRef.current()}
                className={cn('inset-0 z-[55] bg-[var(--overlay-scrim)]', placement)}
            />
            <div
                ref={mergeRefs(panelRef, ref)}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                className={cn(
                    'top-0 bottom-0 z-[60] flex max-w-[90%] flex-col overflow-auto overscroll-contain outline-none',
                    /* The panel scrolls as a whole: a child never shrinks below its
                       content (not even with `min-h-0`), so a `DrawerFooter` inside a
                       long form stays pinned instead of drifting up with the form box. */
                    '[&>*]:min-h-auto',
                    placement,
                    side === 'left' ? 'left-0' : 'right-0',
                    surface === 'dark'
                        ? 'bg-[var(--ink-900)] text-[var(--text-on-dark-2)]'
                        : 'bg-[var(--surface-card)] text-[var(--text-primary)]',
                    side === 'left'
                        ? 'border-r border-[var(--border-default)]'
                        : 'border-l border-[var(--border-default)]',
                    surface === 'dark' && 'border-[var(--ink-600)]',
                    className,
                )}
                style={{
                    width: `${width}px`,
                    boxShadow:
                        elevation === 'rail' ? 'var(--shadow-rail-drawer)' : 'var(--shadow-drawer)',
                    ...style,
                }}
                {...rest}
            >
                {children}
            </div>
        </>
    );

    if (!modal) {
        return layer;
    }

    return createPortal(
        <div ref={setLayerRoot} data-drawer-layer="" className="contents">
            <LayerRootContext.Provider value={layerRoot}>{layer}</LayerRootContext.Provider>
        </div>,
        document.body,
    );
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
    return (value: T | null) => {
        for (const ref of refs) {
            if (typeof ref === 'function') {
                ref(value);
            } else if (ref) {
                ref.current = value;
            }
        }
    };
}

type DrawerCloseProps =
    | {
          /** Shows a close button in the header. */
          onClose: () => void;
          /** Its accessible name, from the feature's `copy.ts` ("Sluiten"). */
          closeLabel: string;
      }
    | { onClose?: undefined; closeLabel?: undefined };

export type DrawerHeaderProps = DrawerCloseProps & {
    /** The visible heading, usually the same words as the drawer's `title`. */
    title: string;
    /** Mono caps line above the heading. */
    eyebrow?: string;
    /** One sentence under the heading. */
    description?: ReactNode;
    /** Brand-sheet tile before the heading. */
    icon?: IconName;
    className?: string;
};

/**
 * The top of a work panel: tile, heading and a close button, pinned while the
 * panel scrolls. Put it first inside a light `Drawer`.
 */
export function DrawerHeader({
    title,
    eyebrow,
    description,
    icon,
    onClose,
    closeLabel,
    className,
}: DrawerHeaderProps) {
    return (
        <div
            className={cn(
                'sticky top-0 z-[1] flex shrink-0 items-start gap-3 border-b border-[var(--border-default)] bg-[var(--surface-card)] py-4 pr-3 pl-5',
                className,
            )}
        >
            {icon ? <IconTile name={icon} size="lg" /> : null}

            <div className="min-w-0 flex-1 self-center">
                {eyebrow ? (
                    <MonoLabel size={10} tone="muted" as="div" className="mb-1">
                        {eyebrow}
                    </MonoLabel>
                ) : null}
                <h2 className="m-0 font-[family-name:var(--font-display)] text-[19px] leading-[1.2] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
                    {title}
                </h2>
                {description ? (
                    <p className="m-0 mt-1 text-[13px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                        {description}
                    </p>
                ) : null}
            </div>

            {onClose ? (
                <IconButton
                    icon="close"
                    variant="ghost"
                    size="md"
                    aria-label={closeLabel}
                    onClick={onClose}
                />
            ) : null}
        </div>
    );
}

export type DrawerFooterProps = ComponentPropsWithRef<'div'>;

/**
 * The actions of a work panel, pinned to the bottom while the panel scrolls and
 * pushed down when the content is short. It may sit inside a `<form>`, so the
 * submit button stays a real submit button.
 */
export function DrawerFooter({ className, children, ...rest }: DrawerFooterProps) {
    return (
        <div
            className={cn(
                'sticky bottom-0 z-[1] mt-auto flex shrink-0 flex-wrap items-center gap-2 border-t border-[var(--border-default)] bg-[var(--surface-card)] px-5 py-3',
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
}
